import { SKIN } from '../types/enums';

export const SIDEBAR_OPTION = {
  SPORTS: 'sports',
  IN_PLAY: 'inPlay',
  IN_PLAY_MATCH: 'inPlayMatch',
  ACCOUNT: 'account',
} as const;

export type SidebarType = (typeof SIDEBAR_OPTION)[keyof typeof SIDEBAR_OPTION];

const SIDEBAR_PRODUCT = {
  SPORTSBOOK: 'sportsbook',
  CASINO: 'casino',
} as const;

type SidebarProduct = (typeof SIDEBAR_PRODUCT)[keyof typeof SIDEBAR_PRODUCT];

const SPORTSBOOK_SIDEBAR_PRODUCTS = [SIDEBAR_PRODUCT.SPORTSBOOK] as const;
const BETTING_SIDEBAR_PRODUCTS = [SIDEBAR_PRODUCT.SPORTSBOOK, SIDEBAR_PRODUCT.CASINO] as const;

export type SidebarConfig = {
  path: string;
  sidebarType: SidebarType;
  hideOnDesktop?: boolean;
  supportedProducts?: readonly SidebarProduct[];
};

type ProductAvailability = {
  hasCasinoAccess: boolean;
  hasSportsbookAccess: boolean;
};

const hasProductAccess: Record<SidebarProduct, (availability: ProductAvailability) => boolean> = {
  [SIDEBAR_PRODUCT.SPORTSBOOK]: ({ hasSportsbookAccess }) => hasSportsbookAccess,
  [SIDEBAR_PRODUCT.CASINO]: ({ hasCasinoAccess }) => hasCasinoAccess,
};

const sportsSidebarConfig = (path: string, options: Pick<SidebarConfig, 'hideOnDesktop'> = {}): SidebarConfig => ({
  path,
  sidebarType: SIDEBAR_OPTION.SPORTS,
  supportedProducts: SPORTSBOOK_SIDEBAR_PRODUCTS,
  ...options,
});

const neobetBettingSidebarConfig = (path: string): SidebarConfig => ({
  path,
  sidebarType: SIDEBAR_OPTION.SPORTS,
  supportedProducts: BETTING_SIDEBAR_PRODUCTS,
});

export const BET365_SIDEBAR_CONFIGS: SidebarConfig[] = [
  { path: '/my-account', sidebarType: SIDEBAR_OPTION.ACCOUNT },
  {
    path: '/in-play/match-view',
    sidebarType: SIDEBAR_OPTION.IN_PLAY_MATCH,
    supportedProducts: SPORTSBOOK_SIDEBAR_PRODUCTS,
  },
  {
    path: '/in-play',
    sidebarType: SIDEBAR_OPTION.IN_PLAY,
    hideOnDesktop: true,
    supportedProducts: SPORTSBOOK_SIDEBAR_PRODUCTS,
  },
  sportsSidebarConfig('/sports'),
  sportsSidebarConfig('/my-bets', { hideOnDesktop: true }),
  sportsSidebarConfig('/casino', { hideOnDesktop: true }),
  sportsSidebarConfig('/promotions'),
  sportsSidebarConfig('/search', { hideOnDesktop: true }),
  sportsSidebarConfig('/current-balance', { hideOnDesktop: true }),
  sportsSidebarConfig('/account-balance', { hideOnDesktop: true }),
  sportsSidebarConfig('/user-list', { hideOnDesktop: true }),
  sportsSidebarConfig('/user-details', { hideOnDesktop: true }),
  sportsSidebarConfig('/'),
];

const GOLA_SIDEBAR_CONFIGS: SidebarConfig[] = [
  {
    path: '/in-play/match-view',
    sidebarType: SIDEBAR_OPTION.IN_PLAY_MATCH,
    supportedProducts: SPORTSBOOK_SIDEBAR_PRODUCTS,
  },
  { path: '/in-play', sidebarType: SIDEBAR_OPTION.IN_PLAY, supportedProducts: SPORTSBOOK_SIDEBAR_PRODUCTS },
  sportsSidebarConfig('/casino'),
  sportsSidebarConfig('/promotions'),
  sportsSidebarConfig('/'),
];

const NEOBET_SIDEBAR_CONFIGS: SidebarConfig[] = [
  neobetBettingSidebarConfig('/'),
];

export const SKINNED_SIDEBAR_CONFIGS: Record<string, SidebarConfig[]> = {
  [SKIN.BET365]: BET365_SIDEBAR_CONFIGS,
  [SKIN.GOLA]: GOLA_SIDEBAR_CONFIGS,
  [SKIN.NEOBET]: NEOBET_SIDEBAR_CONFIGS,
};

export const SKINNED_HEADER_MENU_ALLOWED_ROUTES = {
  [SKIN.BET365]: BET365_SIDEBAR_CONFIGS.map((config) => config.path),
  [SKIN.GOLA]: GOLA_SIDEBAR_CONFIGS.map((config) => config.path),
  [SKIN.NEOBET]: NEOBET_SIDEBAR_CONFIGS.map((config) => config.path),
};

export const getSidebarConfig = (sidebarConfigs: SidebarConfig[], pathname: string, isMobile: boolean) => {
  const currentSidebar = sidebarConfigs.find((config) => pathname.startsWith(config.path));
  if (currentSidebar?.hideOnDesktop && !isMobile) return null;

  return currentSidebar ?? null;
};

export const getSidebarType = (sidebarConfigs: SidebarConfig[], pathname: string, isMobile: boolean) => {
  return getSidebarConfig(sidebarConfigs, pathname, isMobile)?.sidebarType ?? null;
};

export const canRenderSidebar = (sidebarConfig: SidebarConfig, productAvailability: ProductAvailability) => {
  if (!sidebarConfig.supportedProducts?.length) return true;

  return sidebarConfig.supportedProducts.some((product) => hasProductAccess[product](productAvailability));
};

export const drawerWidth = 268;
export const drawerWidthXL = 320;
export const accountDrawerWidth = 268;
export const inPlayDrawerWidth = 320;
export const inPlayDrawerWidthXL = 320;

export const DRAWER_CONFIG = {
  [SIDEBAR_OPTION.SPORTS]: {
    width: {
      xs: drawerWidth,
      xl: drawerWidthXL,
    },
    backgroundColor: 'neutral.800',
    borderColor: 'neutral.600',
  },
  [SIDEBAR_OPTION.ACCOUNT]: {
    width: accountDrawerWidth,
    backgroundColor: 'primary.main',
    borderColor: 'primary.500',
  },
  [SIDEBAR_OPTION.IN_PLAY]: {
    width: {
      xs: inPlayDrawerWidth,
      xl: inPlayDrawerWidthXL,
    },
    backgroundColor: 'neutral.800',
    borderColor: 'neutral.600',
  },
  [SIDEBAR_OPTION.IN_PLAY_MATCH]: {
    width: {
      xs: inPlayDrawerWidth,
      xl: inPlayDrawerWidthXL,
    },
    backgroundColor: 'neutral.800',
    borderColor: 'neutral.600',
  },
};
